import React from "react";
import { cn } from "@shadcn-in-nx/utils";
import {
    User, Bot, Wrench, CheckCircle, AlertCircle, ChevronRight,
    FileText, Code, Search, Edit3, Trash2, PlusCircle, FolderSearch, Coins,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const toolIcons = {
    read_files: FileText,
    execute_code: Code,
    create_file: PlusCircle,
    update_file: Edit3,
    edit_file: Edit3,
    delete_file: Trash2,
    search_in_files: Search,
    grep_search: Search,
    glob_search: FolderSearch,
    explore_codebase: FolderSearch,
};

const getMessageIcon = (role, hasError) => {
    switch (role) {
        case "human":
            return <User className="w-4 h-4" />;
        case "assistant":
            return <Bot className="w-4 h-4" />;
        case "tool_call":
        case "tool_response":
            return hasError ? (
                <AlertCircle className="w-4 h-4" />
            ) : (
                <Wrench className="w-4 h-4" />
            );
        default:
            return <CheckCircle className="w-4 h-4" />;
    }
};

const getItemStyle = (role, isSelected, hasError) => {
    const base = "cursor-pointer transition-all border-l-4";
    if (isSelected) return cn(base, "bg-blue-50 border-l-blue-500");
    if (hasError) return cn(base, "hover:bg-red-50 border-l-red-300");
    switch (role) {
        case "human":
            return cn(base, "hover:bg-blue-50 border-l-blue-200");
        case "assistant":
            return cn(base, "hover:bg-purple-50 border-l-purple-200");
        case "tool_call":
        case "tool_response":
            return cn(base, "hover:bg-amber-50 border-l-amber-200");
        default:
            return cn(base, "hover:bg-gray-50 border-l-gray-200");
    }
};

const ToolCallPills = ({ items }) => {
    const toolCalls = items.filter(i => i.role === "tool_call");
    const uniqueTools = [...new Set(toolCalls.map(t => t.toolName))];

    return (
        <div className="flex flex-wrap gap-1 mt-1.5">
            {uniqueTools.slice(0, 3).map((toolName, i) => {
                const Icon = toolIcons[toolName] || Wrench;
                const count = toolCalls.filter(t => t.toolName === toolName).length;
                return (
                    <span
                        key={i}
                        className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded"
                    >
                        <Icon className="w-3 h-3" />
                        {toolName.replace(/_/g, " ")}
                        {count > 1 && <span className="text-amber-500">x{count}</span>}
                    </span>
                );
            })}
            {uniqueTools.length > 3 && (
                <span className="text-xs text-gray-500">+{uniqueTools.length - 3} more</span>
            )}
        </div>
    );
};

const CreditBadge = ({ credits }) => {
    if (!credits) return null;
    const color = credits >= 50 ? "text-red-600 bg-red-100"
        : credits >= 25 ? "text-yellow-600 bg-yellow-100"
        : "text-gray-500 bg-gray-100";
    return (
        <span className={cn("inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded", color)}>
            <Coins className="w-2.5 h-2.5" />{credits.toFixed(2)}
        </span>
    );
};

const FlowItem = ({ item, isSelected, onClick }) => {
    const hasError = item.type === "tool_group" && item.items.some(
        i => i.functionResponse?.success === false || i.functionResponse?.error
    );

    const getPreview = () => {
        if (item.type === "tool_group") {
            const toolCalls = item.items.filter(i => i.role === "tool_call");
            const successCount = item.items.filter(i =>
                i.role === "tool_response" &&
                i.functionResponse?.success !== false &&
                !i.functionResponse?.error
            ).length;
            return (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="text-green-600">{successCount} ok</span>
                    {hasError && <span className="text-red-600">{toolCalls.length - successCount} failed</span>}
                </div>
            );
        }
        return (
            <div className="text-xs text-gray-500 truncate max-w-[180px]">
                {item.content?.substring(0, 60)}
                {item.content?.length > 60 && "..."}
            </div>
        );
    };

    return (
        <div
            className={cn(
                "p-2 rounded-r-lg mb-1",
                getItemStyle(item.role || item.type, isSelected, hasError)
            )}
            onClick={onClick}
        >
            <div className="flex items-start gap-2">
                <div className={cn(
                    "p-1 rounded-full flex-shrink-0",
                    hasError ? "bg-red-100 text-red-600" :
                    item.role === "human" ? "bg-blue-100 text-blue-600" :
                    item.role === "assistant" ? "bg-purple-100 text-purple-600" :
                    "bg-amber-100 text-amber-600"
                )}>
                    {getMessageIcon(item.role || "tool_call", hasError)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                        <span className="font-medium text-xs text-gray-800">
                            {item.type === "tool_group"
                                ? `${item.items.filter(i => i.role === "tool_call").length} Tool Calls`
                                : item.role === "human"
                                ? "User"
                                : "Assistant"}
                        </span>
                        <ChevronRight className={cn(
                            "w-3 h-3 transition-transform",
                            isSelected ? "text-blue-500 rotate-90" : "text-gray-400"
                        )} />
                    </div>
                    {item.type === "tool_group" ? (
                        <ToolCallPills items={item.items} />
                    ) : (
                        getPreview()
                    )}
                </div>
            </div>
            <div className="flex items-center justify-between mt-1 pl-7">
                <span className="text-[10px] text-gray-400">
                    {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                </span>
                <CreditBadge credits={item.creditsUsed} />
            </div>
        </div>
    );
};

export default function ConversationFlow({ items, selectedIndex, onSelect }) {
    return (
        <div className="space-y-1">
            {items.map((item, index) => (
                <FlowItem
                    key={item.id || index}
                    item={item}
                    isSelected={selectedIndex === index}
                    onClick={() => onSelect(index)}
                />
            ))}
            {items.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                    No messages in this conversation
                </div>
            )}
        </div>
    );
}
